import React, { useState, useEffect } from 'react';
import {
  Facebook,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  LogOut,
  User,
  Shield,
  Database,
  FileText,
  MapPin,
  Download,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';
import facebookPagesService from '../../../services/integrations/facebookPagesService';
import FacebookFormFieldMapping from './FacebookFormFieldMapping';
import FacebookLeadSync from './FacebookLeadSync';

interface FacebookPage {
  id: string;
  name: string;
  category: string;
  picture?: string;
  has_leadgen_access: boolean;
  access_token: string;
}

interface ConnectedPage {
  page_id: string;
  page_name: string;
  connected_at: string;
  facebook_user_name?: string;
  facebook_user_email?: string;
}

interface FacebookLeadForm {
  id: number;
  uuid: string;
  form_id: string;
  form_name: string;
  form_status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  page_id: string;
  created_time?: string;
  field_mapping?: any;
  last_sync_at?: string;
  total_leads_synced: number;
  is_active: boolean;
  formFields?: FacebookFormField[];
}

interface FacebookFormField {
  id: number;
  uuid: string;
  facebook_field_key: string;
  facebook_field_label?: string;
  facebook_field_type: string; // Can be any of the Facebook field types
  is_required: boolean;
  field_order: number;
  mapped_crm_column?: string;
  is_active: boolean;
}

const FacebookPagesSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isFetchingPages, setIsFetchingPages] = useState(false);
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [connectedPage, setConnectedPage] = useState<ConnectedPage | null>(null);
  const [showPages, setShowPages] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [facebookUser, setFacebookUser] = useState<{
    id: string;
    name: string;
    email: string;
    picture?: string;
  } | null>(null);
  const [leadForms, setLeadForms] = useState<FacebookLeadForm[]>([]);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FacebookLeadForm | null>(null);
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [selectedSyncForm, setSelectedSyncForm] = useState<FacebookLeadForm | null>(null);
  const [showLeadSync, setShowLeadSync] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setIsLoading(true);
    try {
      // Check if Facebook is authenticated (IntegrationSetting exists)
      const authResponse = await facebookPagesService.checkFacebookAuth();

      // Check for connected page (CrmClientSocialAccounts with isSubscribed: true)
      const pageResponse = await facebookPagesService.getConnectedPage();

      if (authResponse.success && authResponse.data?.is_authenticated) {
        setIsAuthenticated(true);
        setFacebookUser(authResponse.data.facebook_user || null);

        if (pageResponse.success && pageResponse.data) {
          setConnectedPage(pageResponse.data);
          // Load cached forms on initial load
          loadCachedForms();
        } else {
          setConnectedPage(null);
        }
      } else {
        setIsAuthenticated(false);
        setConnectedPage(null);
        setFacebookUser(null);
      }
    } catch (error) {
      console.error('Error checking connection:', error);
      setIsAuthenticated(false);
      setConnectedPage(null);
      setFacebookUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectFacebook = async () => {
    setIsConnecting(true);
    try {
      const response = await facebookPagesService.generateOAuthUrl();

      if (response.success && response.data) {
        window.location.href = response.data.oauth_url;
      } else {
        toast.error('Failed to generate OAuth URL');
        setIsConnecting(false);
      }
    } catch (error) {
      console.error('Error connecting to Facebook:', error);
      toast.error('Failed to connect to Facebook');
      setIsConnecting(false);
    }
  };

  // Load cached forms from database (fast)
  const loadCachedForms = async () => {
    setIsLoadingForms(true);
    try {
      const response = await facebookPagesService.getLeadForms(true); // Pass cached_only=true
      if (response.success && response.data) {
        setLeadForms(response.data);
        setLastError(null); // Clear error on success
      } else {
        const errorMessage = response.message || 'No lead forms found in database';
        console.log('No cached forms found:', errorMessage);
        // Don't show error toast for cached data, just log it
      }
    } catch (error: any) {
      console.error('Error loading cached forms:', error);
      // Don't show error for cached data failure
    } finally {
      setIsLoadingForms(false);
    }
  };

  // Refresh forms from Facebook API (slower, shows errors)
  const refreshFormsFromFacebook = async () => {
    setIsLoadingForms(true);
    try {
      const response = await facebookPagesService.getLeadForms();
      if (response.success && response.data) {
        setLeadForms(response.data);
        setLastError(null); // Clear error on success
        toast.success('Forms refreshed from Facebook');
      } else {
        const errorMessage = response.message || 'Failed to refresh lead forms';
        console.error('Lead forms API error:', errorMessage);

        // Store the error for persistent display
        setLastError(errorMessage);

        // Show the exact Facebook Graph API error to the user
        toast.error(`Facebook API Error: ${errorMessage}`, {
          autoClose: 8000, // Show longer for complex error messages
        });
      }
    } catch (error: any) {
      console.error('Error refreshing lead forms:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to refresh lead forms';

      // Store the error for persistent display
      setLastError(errorMessage);

      // Show the exact error from the API
      toast.error(`Facebook API Error: ${errorMessage}`, {
        autoClose: 8000,
      });
    } finally {
      setIsLoadingForms(false);
    }
  };

  const handleFormFieldMapping = (form: FacebookLeadForm) => {
    setSelectedForm(form);
    setShowFieldMapping(true);
  };

  const handleSaveMapping = async (formId: string, fieldMapping: any) => {
    try {
      const response = await facebookPagesService.saveFormMapping(formId, fieldMapping);
      if (response.success) {
        toast.success('Field mapping saved successfully!');
        // Refresh forms to get updated mapping
        await loadCachedForms();
        setShowFieldMapping(false);
        setSelectedForm(null);
      } else {
        toast.error(response.message || 'Failed to save mapping');
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Failed to save field mapping');
    }
  };

  const handleSyncLeads = (form: FacebookLeadForm) => {
    setSelectedSyncForm(form);
    setShowLeadSync(true);
  };

  const handleLeadSync = async (formId: string) => {
    try {
      const result = await facebookPagesService.syncLeads(formId);

      if (result.success && result.data) {
        const { synced, duplicates, errors } = result.data;
        toast.success(
          `Sync completed: ${synced} leads synced, ${duplicates} duplicates, ${errors} errors`
        );
      } else {
        toast.error(result.message || 'Failed to sync leads');
      }

      // Refresh forms to get updated sync statistics
      await loadCachedForms();
    } catch (error: any) {
      console.error('Error syncing leads:', error);
      toast.error(error.response?.data?.message || 'Failed to sync leads');
    }
  };

  const handleToggleFormActive = async (formId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      await facebookPagesService.toggleFormActive(formId, newStatus);

      toast.success(`Form ${newStatus ? 'activated' : 'deactivated'} successfully`);

      // Update local state
      setLeadForms(prevForms =>
        prevForms.map(form =>
          form.form_id === formId ? { ...form, is_active: newStatus } : form
        )
      );
    } catch (error: any) {
      console.error('Error toggling form status:', error);
      toast.error(error.response?.data?.message || 'Failed to update form status');
    }
  };

  const handleOAuthCallback = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const facebookConnected = urlParams.get('facebook_connected');

    if (error) {
      toast.error(`Facebook authentication failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (facebookConnected === 'true') {
      toast.success('Facebook connected successfully!');
      setIsAuthenticated(true);
      // Don't auto-load pages, make it manual
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const loadPages = async () => {
    setIsFetchingPages(true);
    try {
      const response = await facebookPagesService.getPages();
      if (response.success && response.data) {
        setPages(response.data);
        setShowPages(true);
      } else {
        toast.error(response.message || 'Failed to load pages');
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Failed to load Facebook Pages');
    } finally {
      setIsFetchingPages(false);
    }
  };

  const handleSubscribePage = async (page: FacebookPage) => {
    setIsLoading(true);
    try {
      const response = await facebookPagesService.subscribePage(
        page.id,
        page.access_token,
        page.name
      );

      if (response.success) {
        toast.success(`Successfully connected to ${page.name}!`);
        setConnectedPage({
          page_id: page.id,
          page_name: page.name,
          connected_at: new Date().toISOString()
        });
        setShowPages(false);
      } else {
        toast.error(response.message || 'Failed to connect page');
      }
    } catch (error) {
      console.error('Error subscribing to page:', error);
      toast.error('Failed to connect page');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnectFacebook = async () => {
    setIsLoading(true);
    try {
      const response = await facebookPagesService.disconnect();

      if (response.success) {
        toast.success('Facebook Pages integration disconnected');
        setIsAuthenticated(false);
        setConnectedPage(null);
        setPages([]);
        setShowPages(false);
      } else {
        toast.error(response.message || 'Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting Facebook:', error);
      toast.error('Failed to disconnect Facebook integration');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle OAuth callback on component mount
  useEffect(() => {
    handleOAuthCallback();
  }, []);

  if (isLoading && !isConnecting) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-white rounded-lg shadow border border-gray-200 p-6 m-6 mb-0">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2 rounded-lg">
              <Facebook className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Facebook Pages Integration</h1>
              <p className="text-sm text-gray-600">Connect Facebook Pages to retrieve leads automatically</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-500" />
              <span className="text-gray-600">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white rounded-lg shadow border border-gray-200 p-6 mx-6 mt-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2 rounded-lg">
            <Facebook className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facebook Pages Integration</h1>
            <p className="text-sm text-gray-600">Connect Facebook Pages to retrieve leads automatically</p>
          </div>
        </div>

        {/* Connection Status */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            {isAuthenticated && connectedPage ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                <div>
                  <span className="font-medium text-gray-900">Connected to: {connectedPage.page_name}</span>
                  <p className="text-sm text-green-600 font-medium">
                    Successfully connected and subscribed to lead generation events
                  </p>
                  <p className="text-sm text-gray-600">
                    Connected on {new Date(connectedPage.connected_at).toLocaleDateString()}
                  </p>
                  {connectedPage.facebook_user_name && (
                    <p className="text-sm text-gray-500">
                      By: {connectedPage.facebook_user_name} {connectedPage.facebook_user_email && `(${connectedPage.facebook_user_email})`}
                    </p>
                  )}
                </div>
              </>
            ) : isAuthenticated ? (
              <>
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                <div>
                  <span className="font-medium text-gray-900">Facebook Connected</span>
                  <p className="text-sm text-gray-600">Choose a page to connect for lead retrieval</p>
                  {facebookUser && (
                    <p className="text-sm text-gray-500">
                      Account: {facebookUser.name} ({facebookUser.email})
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                <div>
                  <span className="font-medium text-gray-900">Not Connected</span>
                  <p className="text-sm text-gray-600">Connect your Facebook account to get started</p>
                </div>
              </>
            )}
          </div>

          <div className="flex space-x-3">
            {!isAuthenticated ? (
              <button
                onClick={handleConnectFacebook}
                disabled={isConnecting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isConnecting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Facebook className="h-4 w-4" />
                )}
                <span>{isConnecting ? 'Connecting...' : 'Connect Facebook'}</span>
              </button>
            ) : (
              <>
                <button
                  onClick={loadPages}
                  disabled={isFetchingPages}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isFetchingPages ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4" />
                  )}
                  <span>{isFetchingPages ? 'Fetching Pages...' : (connectedPage ? 'Change Page' : 'Connect Page')}</span>
                </button>
                <button
                  onClick={handleConnectFacebook}
                  disabled={isLoading}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  title="Refresh token to extend expiration"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Refresh Token</span>
                </button>
                <button
                  onClick={handleDisconnectFacebook}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  title="Disconnect Facebook integration completely"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Disconnect</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
      {/* Loading overlay when fetching pages from Facebook */}
      {isFetchingPages && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fetching Your Facebook Pages</h3>
              <p className="text-sm text-gray-600">
                We're retrieving your Facebook Pages from Facebook Graph API...
              </p>
              <div className="flex items-center justify-center space-x-2 mt-3">
                <Facebook className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-gray-500">Connected to Facebook</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pages List */}
      {showPages && pages.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Choose a Facebook Page</h2>
            <span className="text-sm text-gray-500">{pages.length} pages available</span>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {pages.map((page) => (
              <div
                key={page.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center space-x-4">
                  {page.picture ? (
                    <img
                      src={page.picture}
                      alt={page.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">{page.name}</h3>
                    <p className="text-sm text-gray-600">{page.category}</p>
                    <p className="text-xs text-gray-500">ID: {page.id}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      {page.has_leadgen_access ? (
                        <>
                          <Shield className="h-4 w-4 text-green-500" />
                          <span className="text-xs text-green-600">Lead access available</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-xs text-red-600">No lead access</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => handleSubscribePage(page)}
                  disabled={!page.has_leadgen_access || isLoading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Database className="h-4 w-4" />
                  <span>Connect</span>
                </button>
              </div>
            ))}
          </div>

          {pages.length === 0 && (
            <div className="text-center py-8">
              <Facebook className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No Facebook Pages found</p>
              <p className="text-sm text-gray-500 mt-1">
                Make sure you have pages with lead generation access
              </p>
            </div>
          )}
        </div>
      )}

      {/* How it works - Compact Section */}
      {connectedPage && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <div className="bg-blue-500 rounded-full p-1">
              <CheckCircle className="h-3 w-3 text-white" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 text-sm">Auto Lead Capture Active</h4>
              <p className="text-xs text-blue-700 mt-1">
                Leads from Facebook forms are automatically synced to your CRM dashboard
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lead Gen Forms Section */}
      {connectedPage && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Lead Generation Forms</h2>
                <p className="text-sm text-gray-600">Map form fields to CRM columns and sync leads</p>
              </div>
            </div>
            <button
              onClick={refreshFormsFromFacebook}
              disabled={isLoadingForms}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingForms ? 'animate-spin' : ''}`} />
              <span>{isLoadingForms ? 'Refreshing...' : 'Refresh from Facebook'}</span>
            </button>
          </div>

          {/* Error Display */}
          {lastError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-red-900">Facebook API Error</h3>
                    <p className="text-sm text-red-700 mt-1 font-mono bg-red-100 p-2 rounded">
                      {lastError}
                    </p>
                    {lastError.includes('pages_manage_ads') && (
                      <p className="text-sm text-red-700 mt-2">
                        <strong>Solution:</strong> Please disconnect and reconnect your Facebook account to get the required permissions.
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setLastError(null)}
                  className="p-1 text-red-600 hover:text-red-700"
                  title="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}


          {isLoadingForms ? (
            <div className="flex items-center justify-center h-32">
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-5 w-5 animate-spin text-purple-500" />
                <span className="text-gray-600">Loading lead generation forms...</span>
              </div>
            </div>
          ) : leadForms.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Form Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fields
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Sync
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {leadForms.map((form) => (
                    <tr key={form.id} className={`hover:bg-gray-50 ${!form.is_active ? 'opacity-50 bg-gray-50' : ''}`}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleFormActive(form.form_id, form.is_active)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            form.is_active ? 'bg-purple-600' : 'bg-gray-300'
                          }`}
                          title={form.is_active ? 'Click to deactivate' : 'Click to activate'}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              form.is_active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className={`font-medium ${form.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                              {form.form_name}
                            </span>
                            {!form.is_active && (
                              <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded-full">
                                Inactive
                              </span>
                            )}
                            {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                Mapped
                              </span>
                            )}
                          </div>
                          <p className={`text-xs font-mono mt-1 ${form.is_active ? 'text-gray-500' : 'text-gray-400'}`}>
                            ID: {form.form_id}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          form.form_status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : form.form_status === 'PAUSED'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {form.form_status}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-sm ${form.is_active ? 'text-gray-600' : 'text-gray-400'}`}>
                        {form.formFields ? `${form.formFields.length} fields` : 'N/A'}
                      </td>
                      <td className={`px-4 py-3 text-sm ${form.is_active ? 'text-gray-600' : 'text-gray-400'}`}>
                        {form.last_sync_at ? new Date(form.last_sync_at).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleFormFieldMapping(form)}
                            disabled={!form.is_active}
                            className={`px-3 py-1 text-white text-xs rounded flex items-center space-x-1 ${
                              form.is_active
                                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                                : 'bg-gray-400 cursor-not-allowed'
                            }`}
                            title={form.is_active ? 'Map form fields to CRM columns' : 'Activate form to map fields'}
                          >
                            <MapPin className="h-3 w-3" />
                            <span>Map Fields</span>
                          </button>

                          {form.field_mapping && Object.keys(form.field_mapping).length > 0 && (
                            <button
                              onClick={() => handleSyncLeads(form)}
                              disabled={!form.is_active}
                              className={`px-3 py-1 text-white text-xs rounded flex items-center space-x-1 ${
                                form.is_active
                                  ? 'bg-green-600 hover:bg-green-700 cursor-pointer'
                                  : 'bg-gray-400 cursor-not-allowed'
                              }`}
                              title={form.is_active ? 'Sync leads from this form' : 'Activate form to sync leads'}
                            >
                              <Download className="h-3 w-3" />
                              <span>Sync</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No lead generation forms found</p>
              <p className="text-sm text-gray-500 mt-1">
                Create lead gen forms on your Facebook Page to see them here
              </p>
            </div>
          )}
        </div>
      )}

      {/* Field Mapping Modal */}
      {selectedForm && (
        <FacebookFormFieldMapping
          form={selectedForm}
          isOpen={showFieldMapping}
          onClose={() => {
            setShowFieldMapping(false);
            setSelectedForm(null);
          }}
          onSave={handleSaveMapping}
        />
      )}

      {/* Lead Sync Modal */}
      {selectedSyncForm && (
        <FacebookLeadSync
          form={selectedSyncForm}
          isOpen={showLeadSync}
          onClose={() => {
            setShowLeadSync(false);
            setSelectedSyncForm(null);
          }}
          onSync={handleLeadSync}
        />
      )}
        </div>
      </div>
    </div>
  );
};

export default FacebookPagesSettings;
